/**
 * Sistema de auto-reload cuando hay versión nueva desplegada.
 *
 * Problema que resuelve:
 *   En mobile, cuando Chrome mete una tab en background y la restaura más
 *   tarde, NO hace fetch del HTML. La tab queda congelada en el bundle JS
 *   que tenía cargado en memoria — aunque hayamos deployado N versiones
 *   nuevas, el usuario sigue viendo la vieja hasta que hace F5 manual.
 *   Esto es BFCache + session restore, comportamiento esperado del browser
 *   para que las pestañas reabran "instant". El header `no-store` del HTML
 *   no fuerza un fetch en esa restauración — solo evita disk cache.
 *
 * Estrategia:
 *   1. Al cargar la app, capturamos el hash del bundle principal que está
 *      ejecutándose (lo leemos del DOM, del src del <script> que cargó
 *      main.jsx — formato Vite: /assets/index-XXXX.js).
 *   2. Cuando la app vuelve a estar visible (pageshow restorado de BFCache
 *      o visibilitychange a 'visible'), pedimos /index.html actual con
 *      cache-bust y extraemos el hash del bundle de ese HTML.
 *   3. Si los hashes no coinciden, hay versión nueva → window.location.reload().
 *
 * Por qué leemos del DOM en vez de inyectar el hash en build-time:
 *   - Cero dependencias de Vite plugins o env vars custom.
 *   - El hash del DOM es la fuente de verdad ABSOLUTA de "qué bundle está
 *     corriendo ahora mismo", no una versión que pudimos inyectar mal.
 *
 * Throttling: máximo un check cada 30s. Sin esto, si el usuario alterna
 *   apps rápido (común en mobile), pegaríamos al server muchas veces.
 *
 * Failsafe: si por alguna razón no podemos leer el hash actual del DOM,
 *   NO arrancamos el sistema. Mejor "el usuario sigue viendo la versión
 *   vieja hasta F5" que "loop infinito de reloads".
 */

const CHECK_THROTTLE_MS = 30_000;
// Matchea: /assets/index-XXXXXXXX.js (XXXX = hash alfanumérico de Vite).
const SCRIPT_HASH_REGEX = /\/assets\/index-([A-Za-z0-9_-]+)\.js/;

let currentHash = null;
let lastCheckAt = 0;
let reloadInFlight = false;

/**
 * Lee el hash del bundle principal del DOM actual.
 * Busca el primer <script src="/assets/index-XXXX.js"> presente.
 * Retorna el hash o null si no encuentra.
 */
function readCurrentBundleHash() {
  const scripts = document.querySelectorAll('script[src]');
  for (const script of scripts) {
    const match = script.src.match(SCRIPT_HASH_REGEX);
    if (match) return match[1];
  }
  return null;
}

/**
 * Fetcha /index.html del server (con cache-bust) y extrae el hash del bundle.
 * Retorna null si falla (network down, parsing falla, etc) — en ese caso
 * el caller no hace nada (mejor no recargar que recargar por error).
 */
async function fetchLatestBundleHash() {
  try {
    const url = `/index.html?_vcheck=${Date.now()}`;
    const res = await fetch(url, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const html = await res.text();
    const match = html.match(SCRIPT_HASH_REGEX);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Si pasó suficiente tiempo desde el último check, fetcha la versión actual
 * y la compara con la que está corriendo. Si difieren, recarga.
 */
async function maybeCheckAndReload() {
  if (reloadInFlight) return;
  const now = Date.now();
  if (now - lastCheckAt < CHECK_THROTTLE_MS) return;
  lastCheckAt = now;

  const latest = await fetchLatestBundleHash();
  if (!latest || !currentHash) return;
  if (latest === currentHash) return;

  // Versión nueva detectada → reload.
  // window.location.reload() sin argumentos es suficiente: el HTML viene
  // con header `no-store`, así que el browser lo pide fresco, y los assets
  // hasheados nuevos vienen con filenames distintos (Vite los renombra
  // en cada build), así que el browser los baja sí o sí.
  reloadInFlight = true;
  window.location.reload();
}

/**
 * Arranca el sistema de auto-reload por versión.
 *
 * Solo activa en PROD — en dev:
 *   - Los archivos cambian todo el tiempo, no tiene sentido chequear.
 *   - El HMR de Vite ya propaga cambios sin reload completo.
 *   - El bundle de dev no tiene hash (sería un loop falso positivo).
 */
export function initVersionCheck() {
  if (!import.meta.env.PROD) return;

  currentHash = readCurrentBundleHash();
  if (!currentHash) {
    // Sin hash legible del DOM → no podemos comparar. No arrancamos
    // listeners para evitar reloads accidentales.
    console.warn(
      '[version-check] No se pudo leer el bundle hash del DOM. Auto-reload desactivado.'
    );
    return;
  }

  // pageshow se dispara al cargar la página Y al restaurarse desde BFCache.
  // event.persisted=true indica restauración (no carga inicial).
  // Esto es el caso típico de "cerré Chrome, lo abrí, la tab volvió como estaba".
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      maybeCheckAndReload();
    }
  });

  // visibilitychange cubre el caso de la PWA en standalone display mode
  // que queda en background (sin BFCache) y vuelve al foreground.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      maybeCheckAndReload();
    }
  });
}
